/**
 * The phone service page (Gabriel 2026-10-21, "mockup is good"; frames A and B of
 * docs/mockups/telify-web/phone-service-page.html): below 768px a service's page is the app's
 * own ServiceDetailScreen with its picker state (useSlotPicker), fed the website's public reads
 * (shared-client-screens D3, rows S1 to S6). This file is the website's data part.
 *
 * The website's slots: the chat card signs a visitor in with Telegram (a signed-in client's
 * opens the messages, web-account-pages D8; S1), Back returns where the visitor came from on
 * this site, or to the studio's home when they arrived from a link (S2), Book session carries
 * the picked time to the checkout, which signs in and holds it (S3), and a time in the link
 * (`?start=`) opens with that day and time picked (S4). Nothing here reaches the Mini App SDK
 * (scripts/check-web-no-telegram.mjs).
 */
import { useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { SiteServiceDto } from '@telify/shared';

import type { HubServiceSlot } from '@/api/types';
import { DetailLightboxView } from '@/pages/SessionDetailPage/components/DetailLightboxView';
import { addDaysYmd, getClientTimezone, todayYmd } from '@/pages/SessionDetailPage/detailFormat';
import { ServiceDetailScreen } from '@/pages/SessionDetailPage/ServiceDetailScreen';
import { useSlotPicker } from '@/pages/SessionDetailPage/useSlotPicker';

import { useAvailableDates, useDayTimes, useSiteStudio } from '../api/site';
import { useSiteBack } from '../pageMover';
import { toServiceDetail } from './appShapes';
import { usePhoneChat } from './PhoneDetailFrame';

/** The app's booking window (ServiceDetailPage's 90-day horizon), read in two spans the website's dates read allows. */
const HORIZON_DAYS = 90;
const FIRST_SPAN_DAYS = 61;

/** A UTC instant from the address, or null when it is not one. */
function readStart(raw: string | null): string | null {
  if (!raw) return null;
  const at = new Date(raw);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

export function PhoneServicePage({ service }: { service: SiteServiceDto | null }) {
  const navigate = useNavigate();
  const onBack = useSiteBack();
  // The app's service page reads "Secure messaging" under the studio's name.
  const chat = usePhoneChat('Secure messaging');
  const { serviceId } = useParams<{ serviceId: string }>();
  const [search] = useSearchParams();
  const studio = useSiteStudio().data;
  const tz = useMemo(() => getClientTimezone(), []);
  const today = useMemo(() => todayYmd(tz), [tz]);

  // The open days over the app's window, in the visitor's own zone.
  const accepting = service?.acceptingBookings === true;
  const readId = accepting ? serviceId : undefined;
  const near = useAvailableDates(readId, today, addDaysYmd(today, FIRST_SPAN_DAYS), tz);
  const far = useAvailableDates(readId, addDaysYmd(today, FIRST_SPAN_DAYS + 1), addDaysYmd(today, HORIZON_DAYS), tz);
  const available = useMemo(() => new Set([...(near.data?.dates ?? []), ...(far.data?.dates ?? [])]), [near.data, far.data]);

  // The link's time, read once.
  const linkStart = useRef(readStart(search.get('start')));

  const { selectedSlot, picker } = useSlotPicker({
    tz,
    enabled: accepting,
    dates: { available, settled: !near.isPending && !far.isPending, failed: near.isError && far.isError },
    useDay: (date) => {
      const times = useDayTimes(readId, date, tz);
      // While a newly picked day's times load, the last day's stay on screen, dimmed and untappable, as in the
      // app (CWR 2.4.1-C), so the sheet keeps its height (Gabriel 2026-10-21).
      const slots = useMemo<HubServiceSlot[]>(
        () => (times.data ? times.data.times.map((t) => ({ startsAt: t.startsAt, endsAt: t.endsAt, available: true })) : []),
        [times.data],
      );
      return { slots, loading: !times.data && !!date, updating: times.isPlaceholderData && times.data?.date !== date };
    },
    start: linkStart.current ? { startsAt: linkStart.current, held: false } : null,
  });

  const book = () => {
    if (!service || !selectedSlot) return;
    navigate(`/checkout?${new URLSearchParams({ service: service.id, start: selectedSlot.startsAt })}`);
  };

  return (
    <ServiceDetailScreen
      status={service ? 'ready' : 'loading'}
      onBack={onBack}
      service={service ? toServiceDetail(service) : undefined}
      Lightbox={DetailLightboxView}
      chat={studio?.name ? { businessName: studio.name, line: chat.line, onChat: chat.onChat } : null}
      tz={tz}
      picker={picker}
      selectedSlot={selectedSlot}
      book={{ onBook: book, pending: false, error: null }}
    />
  );
}
